import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ApplicationModal from './components/ApplicationModal';
import LanguageSwitcher from '../../components/base/LanguageSwitcher';
import { updateMetaTags, addSchemaOrg, generateWebPageSchema, generateFAQSchema } from '../../utils/seo';

export default function Financing() {
  const { t } = useTranslation('financing');
  const { t: tCommon } = useTranslation('common');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [vehiclePrice, setVehiclePrice] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [loanTerm, setLoanTerm] = useState('60');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [calculatedPayment, setCalculatedPayment] = useState({
    monthlyPayment: 0,
    totalLoanAmount: 0,
    totalInterest: 0,
    totalAmount: 0,
    gst: 0,
    qst: 0,
    totalTaxes: 0,
    totalWithTaxes: 0
  });

  useEffect(() => {
    // Update SEO meta tags
    updateMetaTags({
      title: 'Commercial Vehicle Financing Montreal - Flexible Payment Options | Commercial Truck & Van Center',
      description: 'Get approved for commercial vehicle financing in Montreal. We offer competitive rates, flexible terms, and quick approval for trucks and vans. Traditional financing and commercial lease options available.',
      keywords: 'commercial vehicle financing Montreal, truck financing Quebec, van financing, business vehicle loans, commercial lease, flexible payment options, quick approval financing',
      canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/financing`
    });

    // Add WebPage and FAQ Schema
    const webPageSchema = generateWebPageSchema(
      'FinancingPage',
      'Commercial Vehicle Financing Montreal',
      'Get approved for commercial vehicle financing with competitive rates and flexible terms'
    );

    const faqSchema = generateFAQSchema([
      {
        question: 'What financing options are available?',
        answer: 'We offer traditional financing with rates from 8.9% to 12.9% for 12-84 months, and commercial lease options with rates from 8.9% to 12.9% for 24-60 months.'
      },
      {
        question: 'How long does approval take?',
        answer: 'Most financing applications are approved within 24-48 hours. We offer quick approval process to get you on the road faster.'
      },
      {
        question: 'What documents do I need?',
        answer: 'You will need a valid driver\'s license, proof of income, bank statements, business license (if applicable), proof of work, insurance information, and references.'
      },
      {
        question: 'Can I get financing with bad credit?',
        answer: 'We work with multiple lenders to find financing solutions for various credit situations. Contact us to discuss your specific situation.'
      },
      {
        question: 'Is there a down payment required?',
        answer: 'Down payment requirements vary by financing option. Traditional financing typically requires 10-20%, while commercial leases may require 0-15%.'
      }
    ]);

    const combinedSchema = {
      '@context': 'https://schema.org',
      '@graph': [webPageSchema, faqSchema]
    };

    addSchemaOrg(combinedSchema);
  }, []);

  const calculatePayment = () => {
    const price = parseFloat(vehiclePrice) || 0;
    const down = parseFloat(downPayment) || 0;
    const rate = parseFloat(interestRate) || 0;
    const term = parseInt(loanTerm) || 60;

    if (price <= 0 || rate <= 0 || term <= 0) {
      return;
    }

    // Calculate Quebec taxes
    const gst = price * 0.05; // 5% GST
    const qst = price * 0.09975; // 9.975% QST
    const totalTaxes = gst + qst;
    const totalWithTaxes = price + totalTaxes;

    const loanAmount = totalWithTaxes - down;
    const monthlyRate = rate / 100 / 12;
    const monthlyPayment = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, term)) /
                          (Math.pow(1 + monthlyRate, term) - 1);
    
    const totalAmount = monthlyPayment * term;
    const totalInterest = totalAmount - loanAmount;

    setCalculatedPayment({
      monthlyPayment: Math.round(monthlyPayment),
      totalLoanAmount: loanAmount,
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      gst: Math.round(gst),
      qst: Math.round(qst),
      totalTaxes: Math.round(totalTaxes),
      totalWithTaxes: Math.round(totalWithTaxes)
    });
  };

  const financingOptions = [
    {
      title: t('traditionalFinancing'),
      rate: '8.9% - 12.9%',
      term: '12 - 84 months',
      downPayment: '10% - 20%',
      description: t('traditionalFinancingDesc'),
      features: [t('fixedRates'), t('flexibleTerms'), t('quickApprovalFeature'), t('noPrepayment')]
    },
    {
      title: t('commercialLease'),
      rate: '8.9% - 12.9%',
      term: '24 - 60 months',
      downPayment: '0% - 15%',
      description: t('commercialLeaseDesc'),
      features: [t('lowerPayments'), t('taxDeductible'), t('upgradeFlexibility'), t('maintenancePackages')]
    }
  ];

  const requirements = [
    t('validLicense'),
    t('proofIncome'),
    t('bankStatements'),
    t('businessLicense'),
    t('proofWork'),
    t('insuranceInfo'),
    t('references')
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Language Switcher - Fixed Position */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      {/* Header */}
      <section className="bg-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">{t('title')}</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              {t('subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setSelectedTab('overview')}
              className={`px-6 py-4 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                selectedTab === 'overview'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              {t('overview')}
            </button>
            <button
              onClick={() => setSelectedTab('options')}
              className={`px-6 py-4 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                selectedTab === 'options'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              {t('options')}
            </button>
            <button
              onClick={() => setSelectedTab('calculator')}
              className={`px-6 py-4 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                selectedTab === 'calculator'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              {t('calculator')}
            </button>
            <button
              onClick={() => setSelectedTab('requirements')}
              className={`px-6 py-4 font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                selectedTab === 'requirements'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              {t('requirements')}
            </button>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Overview Tab */}
          {selectedTab === 'overview' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('whyChooseFinancing')}</h2>
                <p className="text-xl text-gray-600">{t('whyChooseFinancingDesc')}</p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-time-line text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{t('quickApproval')}</h3>
                  <p className="text-gray-600">{t('quickApprovalDesc')}</p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-percent-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{t('competitiveRates')}</h3>
                  <p className="text-gray-600">{t('competitiveRatesDesc')}</p>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-settings-line text-2xl text-purple-600"></i>
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{t('flexibleTerms')}</h3>
                  <p className="text-gray-600">{t('flexibleTermsDesc')}</p>
                </div>
              </div>

              <div className="bg-blue-50 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('readyToStart')}</h3>
                <p className="text-gray-700 mb-6">{t('readyToStartDesc')}</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('applyNow')}
                  </button>
                  <button 
                    onClick={() => {
                      const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                      if (widget) widget.click();
                    }}
                    className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('speakWithSpecialist')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Financing Options Tab */}
          {selectedTab === 'options' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('financingOptionsTitle')}</h2>
                <p className="text-xl text-gray-600">{t('financingOptionsDesc')}</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {financingOptions.map((option, index) => (
                  <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-blue-300 transition-colors">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{option.title}</h3>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('interestRate')}</span>
                        <span className="font-semibold text-blue-600">{option.rate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('termLength')}</span>
                        <span className="font-semibold">{option.term}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('downPayment')}</span>
                        <span className="font-semibold">{option.downPayment}</span>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-6">{option.description}</p>
                    <ul className="space-y-2 mb-8">
                      {option.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center gap-3">
                          <i className="ri-check-line text-green-600"></i>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('learnMore')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Calculator Tab */}
          {selectedTab === 'calculator' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('calculatorTitle')}</h2>
                <p className="text-xl text-gray-600">{t('calculatorDesc')}</p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="bg-white p-8 rounded-xl shadow-lg">
                    <h3 className="text-xl font-semibold mb-6">{t('loanDetails')}</h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehiclePrice')}</label>
                        <div className="relative">
                          <span className="absolute left-3 top-3 text-gray-500">$</span>
                          <input 
                            type="number" 
                            value={vehiclePrice}
                            onChange={(e) => setVehiclePrice(e.target.value)}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="50,000"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('downPaymentLabel')}</label>
                        <div className="relative">
                          <span className="absolute left-3 top-3 text-gray-500">$</span>
                          <input 
                            type="number" 
                            value={downPayment}
                            onChange={(e) => setDownPayment(e.target.value)}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="10,000"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('interestRateLabel')}</label>
                        <input 
                          type="number" 
                          step="0.1"
                          value={interestRate}
                          onChange={(e) => setInterestRate(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="7.5"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('loanTermLabel')}</label>
                        <select 
                          value={loanTerm}
                          onChange={(e) => setLoanTerm(e.target.value)}
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="12">12 months</option>
                          <option value="24">24 months</option>
                          <option value="36">36 months</option>
                          <option value="48">48 months</option>
                          <option value="60">60 months</option>
                          <option value="72">72 months</option>
                          <option value="84">84 months</option>
                        </select>
                      </div>
                      
                      <button 
                        onClick={calculatePayment}
                        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {t('calculatePayment')}
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-white p-8 rounded-xl shadow-lg">
                    <h3 className="text-xl font-semibold mb-6">{t('paymentBreakdown')}</h3>
                    <div className="space-y-6">
                      <div className="border-b pb-4">
                        <div className="text-sm text-gray-600 mb-1">{t('monthlyPayment')}</div>
                        <div className="text-3xl font-bold text-blue-600">
                          ${calculatedPayment.monthlyPayment.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-semibold text-gray-900 mb-3">{t('taxBreakdown')}</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('gst')}</span>
                              <span className="font-semibold">${calculatedPayment.gst.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">{t('qst')}</span>
                              <span className="font-semibold">${calculatedPayment.qst.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-gray-900 font-semibold">{t('totalTaxes')}</span>
                              <span className="font-bold">${calculatedPayment.totalTaxes.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('vehiclePriceTaxes')}</span>
                          <span className="font-semibold">${calculatedPayment.totalWithTaxes.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('loanAmount')}</span>
                          <span className="font-semibold">${calculatedPayment.totalLoanAmount.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('totalInterest')}</span>
                          <span className="font-semibold">${calculatedPayment.totalInterest.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between border-t pt-4">
                          <span className="text-gray-900 font-semibold">{t('totalAmount')}</span>
                          <span className="font-bold text-lg">${calculatedPayment.totalAmount.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 p-4 rounded-lg mt-6">
                        <p className="text-sm text-gray-700">
                          <i className="ri-information-line text-blue-600 mr-2"></i>
                          {t('estimateDisclaimer')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Requirements Tab */}
          {selectedTab === 'requirements' && (
            <div>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('requirementsTitle')}</h2>
                <p className="text-xl text-gray-600">{t('requirementsDesc')}</p>
              </div>

              <div className="max-w-3xl mx-auto">
                <div className="bg-white p-8 rounded-xl shadow-lg">
                  <h3 className="text-xl font-semibold mb-6">{t('requiredDocuments')}</h3>
                  <ul className="space-y-4">
                    {requirements.map((requirement, index) => (
                      <li key={index} className="flex items-start gap-4">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-blue-600"></i>
                        </div>
                        <span className="text-gray-700 text-lg">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-8 p-6 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">{t('quickPreApproval')}</h4>
                    <p className="text-gray-700 mb-4">{t('quickPreApprovalDesc')}</p>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('startApplication')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/3a68546ea1fc6bd42c4dffe18390304b/4e0983a2a8baa9b8befdefb7167d7d65.png" 
                alt="JMA Logo" 
                className="h-16 mb-6"
              />
              <p className="text-gray-300 mb-6">{tCommon('footerDesc')}</p>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-600 transition-colors">
                  <i className="ri-facebook-fill"></i>
                </div>
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-600 transition-colors">
                  <i className="ri-instagram-fill"></i>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{tCommon('services')}</h4>
              <ul className="space-y-3 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('vehicleSales')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('tradeIns')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('rentalsService')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('financingService')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{tCommon('inventory')}</h4>
              <ul className="space-y-3 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('boxTrucks')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('cargoVans')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('reeferTrucks')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('tailgateTrucks')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{tCommon('rampTrucks')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{tCommon('contactInfo')}</h4>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line"></i>
                  <span>{tCommon('phone')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line"></i>
                  <span>{tCommon('email')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line mt-1"></i>
                  <span style={{ whiteSpace: 'pre-line' }}>{tCommon('address')}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Commercial Truck &amp; Van Center. {tCommon('allRightsReserved')} | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">{tCommon('websiteBuilder')}</a></p>
          </div>
        </div>
      </footer>

      {/* Application Modal */}
      <ApplicationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
